import React, { useState } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DownloadIcon from "@mui/icons-material/Download";

const ViewMenu = ({ modalOpen, handleClose, recordData, onPageChange }) => {
  const rows = recordData?.data || [];
  const meta = recordData?.meta || {};
  const [loadingCsv, setLoadingCsv] = useState(false);

  const handleViewClick = (rowIndex) => {
    console.log("View clicked for row:", rowIndex);
  };

  // Fetch CSV data from API and download it
  const handleExportCsv = async () => { 
    try {
      setLoadingCsv(true);
      const res = await fetch(
        `/api/v1/exhibitors-list/admin/get-all-exhibitors-list-records/${recordData.data[0].exhibitors_listId}`
      );
      if (!res.ok) throw new Error("Failed to fetch CSV data");

      const data = await res.json(); 

      // Convert JSON to CSV
      const csvHeaders = ["ID", "Exhibitors List Id", "Company Name", "Booth Number", "Company Website", "Created At", "Updated At"];
      const csvRows = data.map((item) => [
        item.id,
        item.exhibitors_listId,
        `"${item.companyName}"`,
        `"${item.boothNumber}"`,
        item.companyWebsite,
        item.createdAt,
        item.updatedAt,
      ]);

      const csvContent =
        [csvHeaders.join(","), ...csvRows.map((row) => row.join(","))].join("\n");

      // Trigger file download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "exhibitors_list.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error("CSV export failed:", error);
    } finally {
      setLoadingCsv(false);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={modalOpen}
      onClose={handleClose}
      PaperProps={{
        sx: { height: "80vh", borderTopLeftRadius: 12, borderTopRightRadius: 12, zIndex: 1700 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Exhibitor Data {meta?.totalRecords ? `(${meta.totalRecords} records)` : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={loadingCsv}
          >
            {loadingCsv ? "Exporting..." : "Export CSV"}
          </Button>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ p: 2, overflow: "auto" }}>
        {rows.length > 0 ? (
          <>
            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell><strong>Exhibitors List Id</strong></TableCell>
                    <TableCell><strong>Company Name</strong></TableCell>
                    <TableCell><strong>Booth No.</strong></TableCell>
                    <TableCell><strong>Company Website</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={row.id || index}>
                      <TableCell>{row.exhibitors_listId}</TableCell>
                      <TableCell>{row.companyName}</TableCell>
                      <TableCell>{row.boothNumber}</TableCell>
                      <TableCell>{row.companyWebsite}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: "#dbeafe",
                            "&:hover": { backgroundColor: "#bfdbfe" },
                          }}
                          title="View"
                          onClick={() => handleViewClick(index)}
                        >
                          <RemoveRedEyeIcon fontSize="small" sx={{ color: "#1d4ed8" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={meta.totalPages || 1}
                page={meta.currentPage || 1}
                onChange={(e, page) => onPageChange?.(page)}
                color="primary"
              />
            </Box>
          </>
        ) : (
          <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
            No records found.
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default ViewMenu;
